<template>
  <AuthLayout
    sidebar-title="Check your messages"
    :sidebar-text="`The link is valid for ${ttlMinutes} minutes. Don't see it? Check your spam folder or click Resend.`"
    :support-phone="supportPhone"
    :is-admin="isAdmin"
  >
    <div class="auth-login__success-icon">
      <AuthIcon name="check" />
    </div>

    <h2 class="auth-login__card-title auth-login__card-title--center" tabindex="-1" data-step-focus>
      Reset link sent
    </h2>

    <p class="auth-login__card-subtitle" style="text-align: center; margin-bottom: 0">
      We sent a secure password reset link to
      <span class="auth-login__masked">{{ maskedDestination }}</span>.
      Click the link to set a new password.
    </p>

    <div v-if="feedback" class="auth-login__success-banner" role="status">
      <AuthIcon name="check" />
      <span>{{ feedback }}</span>
    </div>

    <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

    <div class="auth-login__actions">
      <AuthButton
        icon="refresh"
        :loading="loading"
        :disabled="isCoolingDown"
        @click="emit('resend')"
      >
        {{ isCoolingDown ? `Resend in ${countdown}` : 'Resend the link' }}
      </AuthButton>
      <AuthButton
        v-if="!isAdmin"
        variant="outline"
        :loading="loading"
        :disabled="isCoolingDown"
        @click="emit('send-alternative')"
      >
        {{ isCoolingDown
          ? `Send to ${channel === 'email' ? 'phone' : 'email'} in ${countdown}`
          : `Send to my ${channel === 'email' ? 'phone' : 'email'} instead`
        }}
      </AuthButton>
    </div>

    <p v-if="isCoolingDown" class="auth-login__cooldown-hint">
      Please wait {{ countdown }} before requesting another link.
    </p>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('back')">
      ← Back to sign in
    </button>
  </AuthLayout>
</template>

<script setup>
import { computed } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'
import { maskIdentifier } from '../utils/maskIdentifier.js'

const props = defineProps({
  channel:       { type: String, required: true },
  identifier:    { type: String, required: true },
  ttlMinutes:    { type: Number, default: 15 },
  loading:       { type: Boolean, default: false },
  error:         { type: String,  default: '' },
  feedback:      { type: String,  default: '' },
  isCoolingDown: { type: Boolean, default: false },
  countdown:     { type: String,  default: '1:00' },
  supportPhone:  { type: String,  default: '815-908-2020' },
  isAdmin:       { type: Boolean, default: false },
})

const emit = defineEmits(['resend', 'send-alternative', 'back'])

const maskedDestination = computed(() => {
  if (props.channel === 'sms') {
    return 'your phone'
  }

  return maskIdentifier(props.identifier)
})
</script>
